import React, { useRef } from "react";

import { Divider, Typography } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import IntroductionImg3 from "../../../assets/SvgIcons/IntroductionImg3.svg";
import "../JaldiLab.css";
import {
  DashedBorderStyle,
  captionWithBorders,
  dimensionStyle,
  getTextContentHeading,
  getTextContentWithHeading,
  h1style,
  h2style,
  h3style,
  h4style,
  paraStyle,
  referenceHtml,
  referenceTootKit,
  sectionStyle,
  subHeadingContent,
  tableCaption,
  tableHeading,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import arbitrationImg from "../../../assets/Images/arbitration_room.jpg";
import { styled } from "@mui/material/styles";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { FigureDesc60 } from "../../../utils/tmp/ImageDescription/ArbitrationMediationID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const tableHeadCell = {
  backgroundColor: "#F45B69",
  color: "#fff",
  fontWeight: 700,
  fontSize: "16px",
  border: `1px solid #AAAAAA`,
  verticalAlign: "baseline",
  padding: "15px 10px",
  fontFamily: "Lato",
};

const borderColor = "#AAAAAA";
const specialBorderColor = "#F3F3F4";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#CCE5E5",
    verticalAlign: "baseline",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F45B69",
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: "16px",
    border: `1px solid ${borderColor}`,
    verticalAlign: "baseline",
  },
  [`&.${tableCellClasses.body}`]: {
    fontfamily: "Lato",
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "27px",
    color: "rgb(85, 85, 85)",
    border: `1px solid ${borderColor}`,
    verticalAlign: "baseline",
  },
  "&:first-child": {
    borderLeft: `1px solid ${specialBorderColor}`,
  },
  "&:last-child": {
    borderRight: `1px solid ${specialBorderColor}`,
  },
}));

function ArbitrationMediation({ goToSection }) {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: "Model Video Conferencing Rules, rule 5.",
    },
    {
      content: "Model Video Conferencing Rules, rule 2(xii).",
    },
    {
      content: "The Arbitration and Conciliation Act 1996. ",
    },
    {
      content: "The Mediation Act 2023.",
    },
  ];

  const ObjectivesArea = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>Facilitate efficient functioning of ADR procedures</span>{" "}
        to help reduce the caseload of the District Courts;
      </>,
      <>
        Design the space to{" "}
        <span style={{ color: "#DC002D" }}>promote mediation and arbitration</span> by ensuring that
        the users are comfortable and do not feel intimidated;{" "}
      </>,
      <>
        Ensure that
        <span style={{ color: "#DC002D" }}>
          {" "}
          all spaces within are inviting, open, and accommodating
        </span>{" "}
        for all parties;{" "}
      </>,
      <>
        Provide
        <span style={{ color: "#DC002D" }}> facilities to ensure confidentiality</span> during the
        ADR process;{" "}
      </>,

      <>
        <span style={{ color: "#DC002D" }}>
          {" "}
          Ensure accessibility of the space for diverse user groups
        </span>{" "}
        including Persons with Disabilities, elderly, expectant mothers, children, etc., and
      </>,
      <>
        Equip the space with{" "}
        <span style={{ color: "#DC002D" }}> robust security and fire safety measures.</span>{" "}
      </>,
    ],
    isOrdered: true,
  };

  const userObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Arbitrators",
      "Mediators",
      "Advocates",
      <>
        Coordinator(s) appointed under the Model Video-Conferencing Rules
        {reference(referenceData[0], 1)}
      </>,
      "Litigants",
      "Staff",
      "Court-appointed officials involved in the ADR processes",
      "Family Members",
      <>Other required persons{reference(referenceData[1], 2)}</>,
    ],
    isOrdered: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The Arbitration and Mediation Centre may function as a self-contained unit, independent of the general flow of activities in the Court Complex. ",
      "In such cases where it is not self-sustained, the Centre must be in close proximity to accessible, family-friendly toilets and drinking water facilities. ",
      "The Centre must have spaces which may be closed off to ensure confidentiality during the proceedings.",
    ],
    isOrdered: true,
  };

  const designConsiderationObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        <>
          The relevant provisions of the Arbitration and Conciliation Act (1996)
          {reference(referenceData[2], 3)} and Mediation Act (2023)
          {reference(referenceData[3], 4)} which pertain to infrastructural requirements are:
          <div style={{ paddingBottom: "15px" }}>
            <p className="table-guidance-heading">
              <span className="table-guidline-text">Table 14:</span>
              <br />
              <span style={tableCaption}>
                Design considerations arising from the Arbitration and Conciliation Act (1996) and
                Mediation Act (2023).
              </span>
            </p>

            <TableContainer>
              <Table aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Section</StyledTableCell>
                    <StyledTableCell>Section Title</StyledTableCell>
                    <StyledTableCell>Details</StyledTableCell>
                    <StyledTableCell>Design Consideration</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell
                      colSpan={4}
                      style={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Arbitration and Conciliation Act (1996)
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow>
                    <StyledTableCell>S.18.</StyledTableCell>
                    <StyledTableCell>Equal treatment of parties.</StyledTableCell>
                    <StyledTableCell>
                      The parties shall be treated with equality and each party shall be given a
                      full opportunity to present this case.
                    </StyledTableCell>
                    <StyledTableCell>
                      The arbitration rooms must be of an appropriate size, such that the parties
                      may sit side-by-side as well as facing each other, as required.
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow>
                    <StyledTableCell
                      colSpan={4}
                      style={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Mediation Act (2023)
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow>
                    <StyledTableCell>S.5.</StyledTableCell>
                    <StyledTableCell>Pre-litigation mediation.</StyledTableCell>
                    <StyledTableCell>
                      (5) The Court-annexed mediation centre and an Authority constituted under the
                      Legal Services Authorities Act, 1987, shall maintain a panel of mediators for
                      the purposes of pre-litigation mediation.
                    </StyledTableCell>
                    <StyledTableCell>
                      Separate office spaces must be provided for the panel of mediators at the
                      mediation Centre.
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow>
                    <StyledTableCell>S.15.</StyledTableCell>
                    <StyledTableCell>Conduct of mediation.</StyledTableCell>
                    <StyledTableCell>
                      (2) The mediator shall assist the parties in an independent, neutral and
                      impartial manner in their attempt to reach an amicable settlement of their
                      dispute.
                    </StyledTableCell>
                    <StyledTableCell>
                      The mediation rooms must be designed in a manner to facilitate neutrality
                      (allowing parties to sit side-by-side as well as facing each other) as well as
                      ensuring the comfort of all participants.
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow>
                    <StyledTableCell>S.30.</StyledTableCell>
                    <StyledTableCell>Online mediation.</StyledTableCell>
                    <StyledTableCell>
                      (1) Online mediation including pre-litigation mediation may be conducted at
                      any stage of mediation under this Act, with the written consent of the parties
                      including by the use of electronic form or computer networks but not limited
                      to an encrypted electronic mail service, secure chat rooms or conferencing by
                      video or audio mode or both.
                      <br /> <br />
                      (3) The conduct of online mediation shall be in the circumstances, which
                      ensure that the essential elements of integrity of proceedings and
                      confidentiality are maintained at all times and the mediator may take such
                      appropriate steps in this regard as he deems fit.
                      <br /> <br />
                      (4) Subject to the other provisions of this Act, the mediation communications
                      in the case of online mediation shall, ensure confidentiality of mediation.
                    </StyledTableCell>
                    <StyledTableCell>
                      Facilities must be provided to ensure online mediation, including IT
                      infrastructure such as Internet networks, secure chat rooms and video/audio
                      conferencing.
                      <br />
                      <br />
                      The confidentiality of online procedures must be maintained through provisions
                      for digital security of communications and records. For further details, refer
                      to{" "}
                      <span
                        onClick={() => goToSection("managementPoliciesStrategies", "record")}
                        style={sectionStyle}
                      >
                        {" "}
                        PART D, Section 4.2.B.
                      </span>{" "}
                      of this Handbook.
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>,
        "The Arbitration and Mediation Centre must be equipped with its own reception area. ",
        "It must have its own segregated waiting area for persons opting for mediation or arbitration.",
        "Separate rooms must be provided to conduct arbitration and mediation proceedings. The Centre must have an adequate number of rooms to ensure that multiple proceedings may proceed parallelly.",
        "All spaces within the Centre must be accessible to diverse user groups, and must be equipped with ramps and elevators, accessible signage, tactile paths, etc.",
        "The Centre must also have its own dedicated pantry, a toilet Complex, and drinking water facilities which are accessible to diverse user groups. ",
        "Adequate office spaces must be provided for arbitrators and empanelled mediators. ",
        "The space may be designed using natural elements, adequate windows and lighting, comfortable seating, and indoor plants and greenery to ensure comfort for all users.",
        <>
          Adequate security measures must be implemented in the Centre, as with the rest of the
          Court Complex, to ensure a safe and secure environment. For further details on security
          measures, please refer to{" "}
          <span onClick={() => goToSection("annexes", "HeadingAnnexure3")} style={sectionStyle}>
            {" "}
            Annexure 3
          </span>
        </>,
      ],
    },
    {
      heading: "Waiting Room",
      items: [
        "In instances where the Centre is part of the general flow of functions in a Court Complex, the waiting area must be segregated from the general waiting areas of the Court Complex. ",

        "The waiting area must have dedicated information kiosks providing information regarding the process and benefits of ADR processes.",

        "The area must have information screens providing information regarding arbitrations and mediations scheduled for the day, along with details of the arbitrators/mediators and the specific rooms in which the respective sessions are scheduled to take place",

        "The waiting area may be designed to be a more casual space to ensure ease and comfort for the persons visiting the Centre.",

        <>
          It must be designed to have a play area for children. For further details on the design of
          waiting areas, please refer to{" "}
          <span onClick={() => goToSection("waitingArea", "familyWaiting")} style={sectionStyle}>
            Section 2.2.B.{" "}
          </span>
        </>,
      ],
    },
    {
      heading: "Reception Area",
      items: [
        "The Centre must have its own dedicated reception area.",

        "It must be manned by at least one receptionist, who must have access to information regarding the ongoing and scheduled proceedings. ",

        "The reception area must be designed to be welcoming and non-intimidating for persons participating in the arbitration and mediation processes. ",

        "It must be well-equipped to provide information to the litigants regarding the benefits of ADR processes, and their rights during the procedures.",
      ],
    },
    {
      heading: "Rooms for Arbitration and Mediation",
      items: [
        "The rooms provided for conducting arbitration and mediation must have modular and ergonomic furniture for flexible setups, adjustable partitions, and adequate multimedia equipment.",

        <>
          The rooms must have secure access points and must be acoustically treated by using
          sound-proof materials and techniques to ensure confidentiality. For further details on
          acoustics, please refer to{" "}
          <span style={sectionStyle} onClick={() => goToSection("annexes", "HeadingAnnexure5")}>
            {" "}
            Annexure 5
          </span>
          .{" "}
        </>,

        <>
          The Centre must have adequate rooms of varying sizes to accommodate various configurations
          of parties. For example, in some cases, more than two parties may be necessary for the
          proceedings. In the case of family disputes, consideration must be given for children who
          may be involved in the proceedings. For further details regarding factors to be considered
          for a child-friendly design, please refer to{" "}
          <span style={sectionStyle} onClick={() => goToSection("vwdc", "vulnerable-witness")}>
            Section 2.3.A
          </span>
          .
        </>,
      ],
    },
    {
      heading: "Office Spaces for Arbitrators and Empanelled Mediators",
      items: [
        "Adequate office spaces must be provided for arbitrators and empanelled mediators, which must be well-equipped with ergonomic furniture and requisite IT services. ",

        "A separate room must be provided for the Judge-in-charge of the Arbitration and Mediation Centre.",

        "The offices must have access to accessible sanitary provisions, such as washrooms and drinking water facilities.",

        "The offices must have adequate space to accommodate storage for files, documents, and the arbitrator/mediators’ personal effects.",

        "The offices must have adequate seating for the arbitrator/mediator to interact with either party privately, if necessary.",

        "It must be acoustically treated to avoid noise buildup. Acoustic ceiling materials may be considered.",

        <>
          Acoustic privacy must be ensured for all the closed rooms by using acoustically treated
          partitions and doors. For further details on acoustics, please refer to{" "}
          <span style={sectionStyle} onClick={() => goToSection("annexes", "HeadingAnnexure5")}>
            {" "}
            Annexure 5.
          </span>
        </>,
      ],
    },
    {
      heading: "Other Spaces",
      items: [
        "The Centre must be equipped with a Legal Research Centre providing database access for mediators, arbitrators, and Advocates using the Centre.",

        "The Centre may also have Consultation Rooms for strategizing and settling of cases.",

        <>
          Adequate space may also be provided for a space to undertake training sessions for ADR
          professionals. For further details of a Training Room, refer to{" "}
          <span
            onClick={() => goToSection("conferenceMeetingRoom", "HeadingConferenceRoom")}
            style={sectionStyle}
          >
            Section 2.9.B.
          </span>
        </>,
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="HeadingChapterTwo" style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="Legal" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9. </span>
          Legal Facilities
        </h3>
        <h4 id="arbitration" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9.</span>A. Arbitration and Mediation Centre
        </h4>
        <p style={paraStyle}>
          The Arbitration and Mediation Centre in District Courts is a specialised facility to
          provide alternative dispute resolution (“ADR”) methods, and to foster an efficient,
          confidential, and fair environment for resolving legal disputes outside the Courtroom.
          Mediation is a voluntary and confidential process and hence the space should be designed
          to foster confidence in the process.
        </p>
        <p style={paraStyle}>
          The Arbitration and Mediation Centre must have adequate space for a waiting area,
          dedicated rooms to undertake arbitration and mediation proceedings, and office spaces for
          the empanelled mediators. All spaces must be designed to ensure accessibility,
          confidentiality, and neutrality for parties involved in arbitration and mediation
          processes.
        </p>
        {getTextContentWithHeading(ObjectivesArea)}
        {getTextContentWithHeading(userObject)}
        {getTextContentWithHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationObject)}
        <CaptionWithBorders1
          image={[arbitrationImg]}
          isZoom={true}
          caption="Figure 60: Typical floor plan for the Arbitration & Mediation Centre"
          zoomImgStyle={{ height: "550px" }}
          imageDescription={FigureDesc60}
        />
        <h2 ref={referencesRef} id="arbitrationRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
}

export default ArbitrationMediation;
